import { formatDate } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, from, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Documents } from 'src/app/models/documents.models';
import { Orders } from 'src/app/models/Orders.model';
import { IWsResult } from 'src/app/models/ws_result.model';
import { OrdersService } from 'src/app/services/orders.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';

/**
 * StatusSpedizioniDettaggliComponent — modale dettagli spedizione
 * aperta da StatusSpedizioniComponent (Chunk F.1, route
 * `/status-spedizioni`).
 *
 * Mostra readonly:
 *   • intestazione ordine (numero, data, data evasione)
 *   • cliente + indirizzo di spedizione completo
 *   • corriere + priorità + stato spedizione
 *   • elenco prodotti con quantità
 *   • colli raggruppati per tipo
 *   • documenti allegati
 *   • etichette PDF (Scatola, DDT, Corriere)
 *
 * Phase 1.5 / v2.8.0 — TS cleaned up (Chunk F.2):
 *   • dropped kendo-dialog + kendo-formfield + kendo-label +
 *     kendo-panelbar (× 4) + kendoTextBox (× 12) (82 kendo refs → 0
 *     in HTML),
 *   • dropped 6 input morti: `onStoricoID`, `order_id`, `code` +
 *     campi readonly trasferiti su `order` diretto (no più
 *     spedizioneDetailsForm intermedio inutile),
 *   • dropped 5 handler morti: `productDetails` (bx-info-circle
 *     commented out nel template legacy), `goToProductDetails`,
 *     `goToProds`, `printAllLabels` (mai chiamata),
 *     `clickedId`, `goBack()` (router.navigate da modale, errato),
 *   • dropped 5 state morti: `shipments`, `agent_address`,
 *     `filteredShipments`, `isSearched`, `filterTerm`, `product`,
 *     `pr_code`, `clickedProduct`,
 *   • dropped dialog dimensions hardcoded (`maxlength=250`,
 *     `minWidth=250`, `dialogWidth=1356`, `dialogHeight=1017`),
 *   • dropped 3 console.log/debug,
 *
 *   FIX BUG #1 — data evasione: era `if(formattedClosedDate ===
 *   '01/01/0001'){…} formattedClosedDate = '-'` (assegnazione finale
 *   incondizionata → data sempre nascosta). Ora sentinel handling
 *   via `isClosingEmpty()` con check `0001-01-01`,
 *
 *   FIX BUG #2 — `linkAllLabels` mai popolato (era usato in href
 *   ma `printAllLabels()` mai chiamata). Ora `printAllLabels` è
 *   triggered in load completion così il link è popolato.
 *
 *   • 3 subscribe annidate + Promise.then → forkJoin singolo
 *     (`takeUntilDestroyed`) — memory leak fix,
 *   • cache-wins semantics preservata: documents = cached ?? server,
 *   • switch a inject() + ChangeDetectorRef.
 *
 * Behaviour preserved 1:1:
 *   • lettura dati ordine, gestione boxes raggruppati,
 *   • cache documents (`getOrderDocuments` localStorage) prevale
 *     su HTTP fresh (`getDocuments`) — convenzione legacy.
 */
@Component({
  selector: 'app-status-spedizioni-dettaggli',
  templateUrl: './status-spedizioni-dettaggli.component.html',
  styleUrls: ['./status-spedizioni-dettaggli.component.scss'],
  standalone: false,
})


export class StatusSpedizioniDettaggliComponent implements OnInit {

  // ── Injected services ─────────────────────────────────────────────
  private readonly orderService = inject(OrdersService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  public readonly closeServ = inject(OpenModalService);

  // ── Inputs ────────────────────────────────────────────────────────
  @Input() parentId!: number | string;
  @Input() status_ID = false;

  // ── Data ──────────────────────────────────────────────────────────
  public order!: Orders.IOrder;
  public documents: Documents.Document[] = [];
  public groupedBoxes: { ts_box_type_desc: string; quantity: number }[] = [];

  // ── Computed strings ──────────────────────────────────────────────
  public formattedDate = '—';
  public formattedClosedDate = '—';
  public isLinkToDDT = false;
  public linkToDDT: string | null = null;
  public linkCarrier: string | null = null;
  public linkAllLabels: string | null = null;

  // ── UI state ──────────────────────────────────────────────────────
  public loading = false;
  public openSection: 'products' | 'boxes' | 'docs' | null = 'products';

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle.
  // ──────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loadOrder();
  }

  // ──────────────────────────────────────────────────────────────────
  // Data loading.
  // ──────────────────────────────────────────────────────────────────

  private loadOrder(): void {
    this.loading = true;
    const idStr = String(this.parentId);
    const idNum = +this.parentId;

    // 3 sources in parallel:
    //   - order (HTTP, required)
    //   - serverDocs (HTTP /get_documents/{id})
    //   - cachedDocs (localStorage lookup) — cache wins per legacy convention
    forkJoin({
      order: this.orderService.getOrder(idStr),
      serverDocs: this.orderService.getDocuments(idNum).pipe(
        catchError(() => of([] as Documents.Document[])),
      ),
      cachedDocs: from(this.orderService.getOrderDocuments(idNum)).pipe(
        catchError(() => of(null)),
      ),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ order, serverDocs, cachedDocs }) => {
          this.order = (order as IWsResult).data;
          this.documents = cachedDocs ?? serverDocs ?? [];

          if (this.order) {
            this.groupedBoxes = this.groupBoxes(this.order.boxes ?? []);
            this.formattedDate = this.formatOrEmpty(this.order.or_order_date);
            this.formattedClosedDate = this.isClosingEmpty(
              this.order.or_closing_date,
            )
              ? '—'
              : this.formatOrEmpty(this.order.or_closing_date);

            this.isLinkToDDT =
              (this.order.or_url_ddt?.indexOf('.pdf') ?? -1) > 0;
            this.linkToDDT = this.isLinkToDDT ? this.order.or_url_ddt : null;
            this.linkCarrier =
              this.order.shipment?.os_label_url?.[0]?.sm_label_link ?? null;

            // Box labels link — fetched lazily once order_id is known.
            this.fetchAllBoxLabels();
          }

          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  /** Fetches the combined-box-labels PDF link (used in the
   *  Etichette section). FIX BUG #2 — legacy never called this. */
  private fetchAllBoxLabels(): void {
    const boxes = this.order?.boxes ?? [];
    if (!this.order?.order_id || boxes.length === 0) return;

    this.orderService
      .getAllBoxLabels(
        this.order.order_id,
        boxes.map((_b, i) => i + 1),
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of(null)),
      )
      .subscribe((result) => {
        this.linkAllLabels = result?.box_label_link_pdf ?? null;
        this.cdr.detectChanges();
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Pure helpers.
  // ──────────────────────────────────────────────────────────────────

  /** Returns true if the closing date is the backend sentinel
   *  meaning "no closing yet" (`0001-01-01T...`). */
  isClosingEmpty(value: string | null | undefined): boolean {
    return !value || String(value).startsWith('0001-01-01');
  }

  private formatOrEmpty(value: string | null | undefined): string {
    if (!value) return '—';
    try {
      return formatDate(value, 'dd/MM/yyyy', 'it');
    } catch {
      return '—';
    }
  }

  /** Groups boxes by `ts_box_type_desc` returning `{desc, quantity}`. */
  private groupBoxes(
    boxes: Orders.IOrder['boxes'],
  ): { ts_box_type_desc: string; quantity: number }[] {
    const map = new Map<string, number>();
    for (const b of boxes) {
      const key = b.ts_box_type_desc ?? '—';
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    return Array.from(map.entries()).map(([ts_box_type_desc, quantity]) => ({
      ts_box_type_desc,
      quantity,
    }));
  }

  /** Maps the legacy shipment status code (via vs_details) into a
   *  visual state used by status-pill. Re-implements F.1 statusState
   *  on the IOrder shape (status_cod under vs_details). */
  statusState(): string {
    const code = this.order?.vs_details?.tab_order_status_cod ?? 0;
    if (code === 0) return 'pending';
    if (code >= 100) return 'delivered';
    if (code >= 50) return 'in-transit';
    if (code >= 10) return 'shipped';
    return 'pending';
  }

  // ──────────────────────────────────────────────────────────────────
  // Accordion toggling.
  // ──────────────────────────────────────────────────────────────────

  toggleSection(name: 'products' | 'boxes' | 'docs'): void {
    this.openSection = this.openSection === name ? null : name;
  }

  // ──────────────────────────────────────────────────────────────────
  // Close.
  // ──────────────────────────────────────────────────────────────────

  close(): void {
    this.closeServ.offId();
  }
  
}
